import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Location from 'expo-location';
import { geocode } from '../../utils/geocoding';
import { setLocation } from '../../store/Actions';
import { logError } from '../../utils/logger';
import { useTranslation } from '../../providers/TranslationProvider';

export interface ResolvedUserLocationRegion {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
}

export interface UseResolvedUserLocationResult {
  region: ResolvedUserLocationRegion | null;
  recenter: () => void;
}

// Matches PantryMap.js's centerMap() zoom level -- a real, local-area view rather than the wide
// continental default pantryRegionForUser falls back to when the user's location isn't known yet.
export const LOCAL_AREA_DELTA = { latitudeDelta: 0.225, longitudeDelta: 0.308 };

export interface ResolvedCoords {
  coords: { latitude: number; longitude: number };
}

async function getUserCurrentLocation(): Promise<ResolvedCoords | null> {
  try {
    const { status } = await Location.requestForegroundPermissionsAsync();
    if (status !== 'granted') return null;
    return await Location.getCurrentPositionAsync({
      enableHighAccuracy: true,
      accuracy: Location.Accuracy.High,
    });
  } catch (e) {
    logError(e as Error);
    return null;
  }
}

export async function getZipCodeLocation(zipCode: string | undefined): Promise<ResolvedCoords | null> {
  if (!zipCode) return null;
  try {
    const result = await geocode(`${zipCode} zip code address`);
    if (!result) return null;
    return { coords: { latitude: result.lat, longitude: result.lng } };
  } catch (e) {
    logError(e as Error);
    return null;
  }
}

// Mirrors PantryMap.js's getUserOrZipcodeLocation: GPS first, falling back to the user's ZIP code
// only if GPS is denied/unavailable.
async function resolveUserLocation(zipCode: string | undefined): Promise<ResolvedCoords | null> {
  const gpsLocation = await getUserCurrentLocation();
  if (gpsLocation) return gpsLocation;
  return getZipCodeLocation(zipCode);
}

export function useResolvedUserLocation(zipCode: string | undefined): UseResolvedUserLocationResult {
  const dispatch = useDispatch();
  const { translation } = useTranslation();
  const [region, setRegion] = useState<ResolvedUserLocationRegion | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Only an explicit recenter tap should alert on failure -- mirrors PantryMap.js's
  // handleMapCentering, which alerts, vs. its silent mount-time centerMap() call, which doesn't.
  const isRecenterAttemptRef = useRef(false);
  // Read via a ref (rather than putting `translation` in the effect's deps below) so a change in
  // translation identity alone -- useLocalization's object isn't guaranteed stable across renders
  // -- doesn't re-trigger a location re-resolution that has nothing to do with it.
  const locationServicesMessageRef = useRef(translation.location_services_no_access);
  locationServicesMessageRef.current = translation.location_services_no_access;

  useEffect(() => {
    let cancelled = false;
    const isRecenterAttempt = isRecenterAttemptRef.current;
    isRecenterAttemptRef.current = false;

    (async () => {
      const location = await resolveUserLocation(zipCode);
      if (cancelled) return;

      if (location) {
        // Shared with the rest of the app (pantry list/search distance sorting) via the `pantry`
        // reducer's location state -- see store/reducers/pantry.js's SET_LOCATION/LOAD_PANTRIES
        // handling, and store/Actions.js's searchPantries.
        dispatch(setLocation(location, true));
        setRegion({
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
          ...LOCAL_AREA_DELTA,
        });
      } else if (isRecenterAttempt) {
        // Matches PantryMap.js's handleMapCentering, the legacy equivalent of this same button.
        // eslint-disable-next-line no-alert -- same justification as PantryMap.js's existing call
        alert(locationServicesMessageRef.current);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [zipCode, attempt, dispatch]);

  const recenter = useCallback(() => {
    isRecenterAttemptRef.current = true;
    setAttempt((a) => a + 1);
  }, []);

  return { region, recenter };
}
